import React from 'react';
import { Helmet } from 'react-helmet';
import '../styles/Legal.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-container">
      <Helmet>
        <title>Privacy Policy | aiNutrition</title>
        <meta name="description" content="Privacy Policy for aiNutrition" />
      </Helmet>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            At aiNutrition, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
          </p>
          <p>
            Please read this Privacy Policy carefully. By accessing or using aiNutrition, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>
            <strong>Personal Information:</strong> We may collect personal information that you voluntarily provide to us when you register for aiNutrition, including:
          </p>
          <ul>
            <li>Email address</li>
            <li>Name (if provided)</li>
            <li>Authentication information when using third-party login providers</li>
          </ul>
          <p>
            <strong>Usage Information:</strong> We automatically collect certain information about your device and how you interact with aiNutrition, including:
          </p>
          <ul>
            <li>Device type and operating system</li>
            <li>Browser type and version</li>
            <li>IP address</li>
            <li>Pages visited and features used</li>
            <li>Time and date of visits</li>
            <li>Referring website or application</li>
          </ul>
          <p>
            <strong>User Content:</strong> We collect information that you voluntarily submit to the service, including:
          </p>
          <ul>
            <li>Ingredients you search for</li>
            <li>Saved recipes and meal plans</li>
            <li>Dietary preferences and restrictions</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>
            We use the information we collect for various purposes, including to:
          </p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Process your account registration</li>
            <li>Personalize your experience</li>
            <li>Generate recipe recommendations based on your inputs</li>
            <li>Respond to your requests or inquiries</li>
            <li>Monitor and analyze usage patterns and trends</li>
            <li>Detect, prevent, and address technical issues</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Your Information</h2>
          <p>
            We may share your information in the following situations:
          </p>
          <ul>
            <li><strong>With Service Providers:</strong> We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf.</li>
            <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
            <li><strong>With Your Consent:</strong> We may disclose your personal information for any other purpose with your consent.</li>
            <li><strong>Legal Requirements:</strong> We may disclose your information where required to do so by law or in response to valid requests by public authorities.</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect the security of your personal information. However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
          </p>
        </section>

        <section>
          <h2>6. Your Privacy Rights</h2>
          <p>
            Depending on your location, you may have certain rights regarding your personal information, such as:
          </p>
          <ul>
            <li>The right to access personal information we hold about you</li>
            <li>The right to request correction of inaccurate information</li>
            <li>The right to request deletion of your information</li>
            <li>The right to opt-out of certain data sharing or processing</li>
            <li>The right to withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information provided in the "Contact Us" section.
          </p>
        </section>

        <section>
          <h2>7. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to track activity on our service and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
          </p>
          <p>
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
          </p>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>
            Our service is not directed to anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.
          </p>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <p>
            We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services</h2>
          <p>
            Our service may contain links to third-party websites or services that are not owned or controlled by aiNutrition. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page. You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p>
            Email: privacy@ainutrition.com
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 